import React from "react"
import PropTypes from "prop-types"

import { moneda, numero, piezas } from "../format"
import KpiCard from "./KpiCard"
import MiniKpiCard from "./MiniKpiCard"
import ProgressBar from "./ProgressBar"
import SupplyCoverageCard from "./SupplyCoverageCard"
import DeficitCard from "./DeficitCard"
import WeekEvolutionChart from "./WeekEvolutionChart"

const pct = (parte, total) => (total > 0 ? (parte / total) * 100 : 0)

const OrdersTab = ({ datos, isLoading }) => {
  if (!datos) {
    return (
      <div className="res-card res-empty res-anim">
        {isLoading
          ? "Cargando las cifras del día…"
          : "No se pudieron cargar las cifras del día."}
      </div>
    )
  }

  const {
    demanda,
    pedidos,
    venta,
    entregas,
    disponibilidad,
    deficit,
    cumplimiento,
    incidencias,
    pendientes,
    semana,
  } = datos

  const diferencia = pedidos.hoy - pedidos.ayer

  return (
    <>
      <div className="res-grid-kpi res-anim">
        <KpiCard
          label="Demanda de hoy"
          icono="bx bx-purchase-tag"
          variante="accent"
          valor={numero(demanda.kgs)}
          unidad="kg"
          hint={`${piezas(demanda.piezas)} solicitadas · ${numero(
            demanda.clientes
          )} clientes`}
        />

        <KpiCard
          label="Pedidos de hoy"
          icono="bx bx-package"
          valor={numero(pedidos.hoy)}
        >
          <div className="res-chip-row">
            <span className={`res-chip${diferencia < 0 ? " is-down" : ""}`}>
              {diferencia < 0 ? "▼" : "▲"} {numero(Math.abs(diferencia))}
            </span>
            <span className="res-chip-note">vs ayer</span>
          </div>
        </KpiCard>

        {/* Venta pedida, no conciliada: sale de los pedidos del día, no de los
            movimientos, así que no tiene por qué cuadrar con la pestaña General. */}
        <KpiCard
          label="Venta pedida de hoy"
          icono="bx bx-dollar"
          valor={moneda(venta.total)}
          formato="money"
          hint={`Ticket promedio ${moneda(venta.ticket_promedio)}`}
        />

        <KpiCard
          label="Camiones del día"
          icono="bx bxs-truck"
          valor={numero(entregas.total)}
          hint={`${numero(entregas.autorizadas)} autorizados · ${numero(
            entregas.pendientes
          )} por autorizar`}
        />
      </div>

      <div className="res-grid-duo">
        <SupplyCoverageCard disponibilidad={disponibilidad} />
        <DeficitCard
          deficit={deficit}
          sinCubrir={disponibilidad.kgs_sin_cubrir}
        />
      </div>

      <div className="res-grid-mini res-anim">
        <MiniKpiCard
          label="Pedidos autorizados"
          valor={Math.round(pct(cumplimiento.autorizados, cumplimiento.total))}
          unidad="%"
          aside={`${numero(cumplimiento.autorizados)} / ${numero(
            cumplimiento.total
          )}`}
        >
          <ProgressBar
            porcentaje={pct(cumplimiento.autorizados, cumplimiento.total)}
          />
        </MiniKpiCard>

        <MiniKpiCard
          label="Pedidos con carga completa"
          valor={Math.round(
            pct(cumplimiento.carga_completa, cumplimiento.total)
          )}
          unidad="%"
          aside={`${numero(cumplimiento.carga_completa)} / ${numero(
            cumplimiento.total
          )}`}
        >
          <ProgressBar
            porcentaje={pct(cumplimiento.carga_completa, cumplimiento.total)}
          />
        </MiniKpiCard>

        <MiniKpiCard
          label="Incidencias"
          valor={numero(incidencias.total)}
          aside="partidas"
          tono={incidencias.total > 0 ? "bad" : undefined}
        >
          {incidencias.total > 0 ? (
            <div className="res-tags">
              {incidencias.canceladas > 0 && (
                <span className="res-tag is-bad">
                  {numero(incidencias.canceladas)} canceladas
                </span>
              )}
              {incidencias.recortadas > 0 && (
                <span className="res-tag is-warn">
                  {numero(incidencias.recortadas)} recortadas
                </span>
              )}
              {incidencias.sobrepedidos > 0 && (
                <span className="res-tag is-warn">
                  {numero(incidencias.sobrepedidos)} sobrepedidos
                </span>
              )}
            </div>
          ) : (
            <div className="res-empty-inline">Sin incidencias registradas.</div>
          )}
        </MiniKpiCard>

        <MiniKpiCard
          label="Pedidos pendientes"
          valor={numero(pendientes.total)}
          aside="sin autorizar"
        >
          <div className="res-tags">
            <span className="res-tag">
              {numero(pendientes.partidas_solicitadas)} partidas solicitadas
            </span>
            <span className="res-tag">
              {numero(pendientes.sin_camion)} sin camión
            </span>
          </div>
        </MiniKpiCard>
      </div>

      <WeekEvolutionChart semana={semana} />
    </>
  )
}

OrdersTab.propTypes = {
  datos: PropTypes.object,
  isLoading: PropTypes.bool,
}

export default OrdersTab
